import { FormEvent, useEffect, useMemo, useRef, useState } from "react";

import { CheckIcon, MagnifyingGlassIcon } from "@heroicons/react/20/solid";
import { ArrowPathIcon, QrCodeIcon } from "@heroicons/react/24/outline";
import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";

import { CHANNEL_LOGOS, IInboxNotice, InboxError, InboxNotice } from "../../components/messagesInbox/InboxBits";
import LogReturnModal from "../../components/returns/LogReturnModal";
import { RECEIVED_CHECK_ANCHOR } from "../../components/returns/ReceivedCheck";
import ReturnsTable from "../../components/returns/ReturnsTable";
import ReturnsTiles from "../../components/returns/ReturnsTiles";
import LoadingWheel from "../../components/shared/LoadingWheel";
import { usePageNameContext } from "../../contexts/PageTitleContext";
import { useScannerOverride } from "../../contexts/ScannerContext";
import useCustomerDetails from "../../data/useCustomerDetails";
import { returnPath, RETURNS_PAGE_SIZE, useReturnsList, useReturnsMutations } from "../../data/useReturns";
import { useWarehouseLookup } from "../../data/useReturnsWarehouse";
import { useFeatureEnabled } from "../../hooks/useFeatureFlag";
import { ReturnsChannelFilter, ReturnsListCounts, ReturnsListParams, ReturnsTab } from "../../models/returns/IReturnsApi";
import classNames from "../../utils/classNames";
import formatDateTime from "../../utils/formatDateTime";
import { INBOX_DAY_TIME } from "../../utils/inboxDates";
import isLocalDebug from "../../utils/isLocalDebug";
import PriceFormat from "../../utils/priceFormat";
import { lookupValueFromScan, scanFromText } from "../../utils/returnsWarehouseScan";
import NoAccess from "../Error/NoAccess";

const TABS: { id: ReturnsTab; label: string }[] = [
  { id: "waiting_response", label: "Waiting for your response" },
  { id: "waiting_buyer", label: "Waiting for buyer" },
  { id: "in_transit", label: "In transit" },
  { id: "delivered_back", label: "Delivered back" },
  { id: "refund_due", label: "Refund due" },
  { id: "closed", label: "Closed" },
  { id: "all", label: "All" },
];

const isTab = (value: string | null): value is ReturnsTab => TABS.some((tab) => tab.id === value);

/** Which field of the API's counts each tab reads. */
const COUNT_KEY: Record<ReturnsTab, keyof ReturnsListCounts> = {
  waiting_response: "waitingResponse",
  waiting_buyer: "waitingBuyer",
  in_transit: "inTransit",
  delivered_back: "deliveredBack",
  refund_due: "refundDue",
  closed: "closed",
  all: "all",
};

type RangePreset = "30d" | "90d" | "12m" | "all";

const RANGES: { id: RangePreset; label: string; days?: number }[] = [
  { id: "30d", label: "Last 30 days", days: 30 },
  { id: "90d", label: "Last 90 days", days: 90 },
  { id: "12m", label: "Last 12 months", days: 365 },
  { id: "all", label: "All time" },
];

const CHANNELS: { id: ReturnsChannelFilter; label: string }[] = [
  { id: "all", label: "All" },
  { id: "ebay", label: "eBay" },
  { id: "partpro", label: "PartPro" },
];

function errorText(error: unknown, fallback: string) {
  return error instanceof Error && error.message ? error.message : fallback;
}

/** Typing elsewhere on the page reaches the scanner hook one key at a time; a real scan is a burst. */
const MIN_SCAN_LENGTH = 5;

/**
 * "Scan or type a tag or tracking number": a barcode scanner types like a keyboard and presses
 * Enter, so a scan anywhere on the page or Enter in the box looks the return up and opens it at
 * Received and checked.
 */
function FindReturn() {
  const { t } = useTranslation("returns");
  const navigate = useNavigate();
  const lookup = useWarehouseLookup();
  const [text, setText] = useState("");
  const [error, setError] = useState<string | null>(null);
  // A scan with the box focused arrives twice (the scanner hook and the form's Enter); look it up once.
  const pending = useRef<string | null>(null);

  const find = async (raw: string) => {
    const value = lookupValueFromScan(scanFromText(raw));
    if (!value || pending.current === value) return;
    pending.current = value;
    setError(null);
    try {
      const id = await lookup.mutateAsync(value);
      navigate(`${returnPath(id)}#${RECEIVED_CHECK_ANCHOR}`);
    } catch (err) {
      const message = errorText(err, "");
      setError(/^HTTP Error: (?!404)/.test(message) ? t("Couldn't look that up. Try again in a moment.") : t("No open return matches that."));
    } finally {
      pending.current = null;
    }
  };

  useScannerOverride((scan) => {
    if (scan.raw.trim().length < MIN_SCAN_LENGTH) return;
    setText(scan.raw.trim());
    find(scan.raw);
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    find(text);
  };

  return (
    <form onSubmit={submit} className="flex flex-col gap-0.5">
      <div className="flex items-center gap-1">
        <label className="flex h-8 w-[250px] items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-2.5 text-gray-500 focus-within:border-primary-500">
          <QrCodeIcon className="h-4 w-4 shrink-0" />
          <input
            value={text}
            onChange={(e) => {
              setText(e.target.value);
              setError(null);
            }}
            placeholder={t("Scan or type a tag or tracking number")}
            aria-label={t("Scan or type a tag or tracking number")}
            className="min-w-0 flex-1 border-0 bg-transparent p-0 text-[12px] text-gray-900 placeholder:text-gray-500 focus:ring-0"
          />
        </label>
        <button type="submit" disabled={lookup.isLoading || !text.trim()} className="h-8 rounded-lg border border-gray-200 bg-white px-2.5 text-[12px] font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50">
          {lookup.isLoading ? t("Finding…") : t("Find")}
        </button>
      </div>
      {error && (
        <span role="alert" className="text-[11px] font-medium text-red-700">
          {error}
        </span>
      )}
    </form>
  );
}

/** The Returns page. Needs the "Returns" feature flag; on localhost the flag is relaxed (no sample data – the API is still called). */
export default function ReturnsList() {
  const { features } = useCustomerDetails();
  const enabled = useFeatureEnabled("Returns");

  if (!features.data && !features.isError) {
    return (
      <div className="flex justify-center py-20">
        <LoadingWheel />
      </div>
    );
  }
  if (!enabled && !isLocalDebug()) return <NoAccess />;

  return <ReturnsWorkspace />;
}

function ReturnsWorkspace() {
  const { t } = useTranslation("returns");
  const { setInfo } = usePageNameContext();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const tabParam = searchParams.get("tab");
  const tab: ReturnsTab = isTab(tabParam) ? tabParam : "waiting_response";
  const [channel, setChannel] = useState<ReturnsChannelFilter>("all");
  const [searchText, setSearchText] = useState("");
  const [search, setSearch] = useState("");
  const [range, setRange] = useState<RangePreset>("90d");
  const [page, setPage] = useState(1);
  const [logOpen, setLogOpen] = useState(false);
  const [notice, setNotice] = useState<IInboxNotice | null>(null);
  const [now, setNow] = useState(() => DateTime.utc());

  useEffect(() => {
    setInfo({ name: t("Returns"), desc: t("eBay, PartPro and counter returns in one place") });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- setInfo is a new function each render; set once per language
  }, [t]);

  // Countdown chips tick once a minute.
  useEffect(() => {
    const timer = window.setInterval(() => setNow(DateTime.utc()), 60_000);
    return () => window.clearInterval(timer);
  }, []);

  // Search waits for typing to stop so the API isn't hit per keystroke.
  useEffect(() => {
    const timer = window.setTimeout(() => setSearch(searchText.trim()), 300);
    return () => window.clearTimeout(timer);
  }, [searchText]);

  const from = useMemo(() => {
    const days = RANGES.find((r) => r.id === range)?.days;
    return days ? (now.minus({ days }).toISODate() as string) : "";
  }, [range, now]);

  const params: ReturnsListParams = useMemo(() => ({ tab, channel, search, from, to: "", page, pageSize: RETURNS_PAGE_SIZE }), [tab, channel, search, from, page]);
  const { list } = useReturnsList(params);
  const { sync, logReturn } = useReturnsMutations();

  const setTab = (next: ReturnsTab) => {
    setSearchParams(next === "waiting_response" ? {} : { tab: next }, { replace: true });
    setPage(1);
  };

  const data = list.data;
  const rows = data?.rows ?? [];
  const countFor = (id: ReturnsTab): number | undefined => data?.counts?.[COUNT_KEY[id]];

  const runSync = async () => {
    try {
      const result = await sync.mutateAsync();
      setNotice({
        tone: "green",
        text: result.skipped ? t("eBay was checked a moment ago – try again in a couple of minutes.") : t("Synced with eBay: {{created}} new, {{updated}} updated.", { created: result.created, updated: result.updated }),
      });
    } catch (error) {
      setNotice({ tone: "red", text: errorText(error, t("Couldn't sync with eBay.")) });
    }
  };

  const [logError, setLogError] = useState<string | null>(null);
  const submitLog = async (body: Parameters<typeof logReturn.mutate>[0]) => {
    setLogError(null);
    try {
      const detail = await logReturn.mutateAsync(body);
      setLogOpen(false);
      navigate(`/returns/${detail.id}`);
    } catch (error) {
      setLogError(errorText(error, t("Couldn't log that return.")));
    }
  };

  const open = (id: string) => navigate(`/returns/${id}`, { state: { ids: rows.map((r) => r.id) } });

  const summary = data
    ? tab === "all"
      ? t("{{count}} returns in this range, with closed", { count: data.total })
      : t("{{count}} in this tab · {{all}} in the range with closed", { count: data.total, all: countFor("all") ?? data.total })
    : "";

  const emptyState =
    tab === "waiting_response" ? (
      <div className="flex flex-col items-center gap-2.5 px-6 py-8 text-center">
        <span className="flex h-[52px] w-[52px] items-center justify-center rounded-full bg-green-100 text-green-700">
          <CheckIcon className="h-7 w-7" />
        </span>
        <h2 className="text-[16px] font-semibold text-gray-900">{t("Nothing waiting for a response")}</h2>
        <p className="max-w-[300px] text-[13px] text-gray-500">
          {t("Every open return has been answered within eBay's deadline.")}
          {data && data.tiles.refundDue > 0 && data.tiles.refundDueBy && (
            <>
              {" "}
              {t("Next thing due:")}{" "}
              <strong className="font-semibold text-gray-700">{t("refund of {{amount}} by {{when}}", { amount: PriceFormat(data.tiles.refundDueAmount), when: formatDateTime(data.tiles.refundDueBy, INBOX_DAY_TIME) })}</strong>.
            </>
          )}
        </p>
        <div className="mt-1 flex gap-2">
          {(["waiting_buyer", "refund_due"] as const).map((id) => (
            <button key={id} type="button" onClick={() => setTab(id)} className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-[12px] font-medium text-gray-900 hover:bg-gray-50">
              {t(TABS.find((x) => x.id === id)?.label ?? id)} · {countFor(id) ?? 0}
            </button>
          ))}
        </div>
      </div>
    ) : (
      <p className="py-6 text-center text-[13px] text-gray-500">{t("No returns here.")}</p>
    );

  return (
    <div className="flex min-h-full flex-col">
      <div className="flex flex-col border-b border-gray-200 bg-white px-4">
        <div className="flex flex-wrap items-center gap-2 pt-2">
          <div className="flex gap-0.5 rounded-lg bg-gray-100 p-[3px]">
            {CHANNELS.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => {
                  setChannel(c.id);
                  setPage(1);
                }}
                className={classNames("flex items-center gap-1.5 rounded-md px-2.5 py-1 text-[12px]", channel === c.id ? "bg-white font-semibold text-gray-900 shadow-sm" : "text-gray-700")}
              >
                {c.id !== "all" && <img src={CHANNEL_LOGOS[c.id].src} alt="" className="h-4 w-4 object-contain" />}
                {t(c.label)}
              </button>
            ))}
          </div>
          <label className="flex h-8 w-[260px] items-center gap-1.5 rounded-lg border border-gray-200 bg-gray-50 px-2.5 text-gray-500">
            <MagnifyingGlassIcon className="h-4 w-4 shrink-0" />
            <input
              type="search"
              value={searchText}
              onChange={(e) => {
                setSearchText(e.target.value);
                setPage(1);
              }}
              placeholder={t("Buyer, order no., Tag no., return ID")}
              className="min-w-0 flex-1 border-0 bg-transparent p-0 text-[12px] text-gray-900 placeholder:text-gray-500 focus:ring-0"
            />
          </label>
          <select
            value={range}
            onChange={(e) => {
              setRange(e.target.value as RangePreset);
              setPage(1);
            }}
            aria-label={t("Date range")}
            className="h-8 rounded-lg border border-gray-200 bg-white py-0 pl-2.5 pr-8 text-[12px] text-gray-700 focus:border-primary-500 focus:ring-primary-500"
          >
            {RANGES.map((r) => (
              <option key={r.id} value={r.id}>
                {t(r.label)}
              </option>
            ))}
          </select>
          <button type="button" onClick={runSync} disabled={sync.isLoading} title={t("Check eBay for new returns now")} className="flex h-8 items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-2.5 text-[12px] text-gray-700 hover:bg-gray-50 disabled:opacity-50">
            <ArrowPathIcon className={classNames("h-4 w-4", sync.isLoading && "animate-spin")} /> {t("Sync")}
          </button>
          <FindReturn />
          <button type="button" onClick={() => setLogOpen(true)} className="h-8 rounded-lg bg-primary-600 px-3.5 text-[12px] font-semibold text-white hover:bg-primary-700">
            {t("Log a return")}
          </button>
        </div>
        <nav aria-label={t("Return folders")} className="flex flex-wrap items-stretch gap-0.5">
          {TABS.map((item) => {
            const active = item.id === tab;
            const count = countFor(item.id);
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => setTab(item.id)}
                aria-current={active ? "page" : undefined}
                className={classNames("-mb-px flex min-h-[46px] items-center gap-1.5 whitespace-nowrap border-b-2 px-2.5 text-[13px]", active ? "border-primary-600 font-semibold text-primary-700" : "border-transparent text-gray-700 hover:text-gray-900")}
              >
                {t(item.label)}
                {count !== undefined && count > 0 && (
                  <span className={classNames("text-[11px]", item.id === "waiting_response" ? "rounded-full bg-red-700 px-[7px] font-semibold leading-[18px] text-white" : "text-[12px] text-gray-500")}>{count}</span>
                )}
              </button>
            );
          })}
        </nav>
      </div>

      <div className="flex flex-col gap-3 px-4 py-3.5">
        {notice && (
          <div className="flex items-start gap-2">
            <InboxNotice notice={notice} className="flex-1" />
            <button type="button" onClick={() => setNotice(null)} className="text-[12px] text-gray-500 hover:text-gray-700">
              {t("Dismiss")}
            </button>
          </div>
        )}
        {list.isError && <InboxError text={errorText(list.error, t("Couldn't load returns."))} onRetry={() => list.refetch()} />}
        {!data && list.isLoading && (
          <div className="flex justify-center py-16">
            <LoadingWheel />
          </div>
        )}
        {data && (
          <>
            <ReturnsTiles tiles={data.tiles} reasons={data.reasons90d} onTab={setTab} />
            <ReturnsTable
              rows={rows}
              now={now}
              onOpen={open}
              page={data.page}
              totalPages={data.totalPages}
              total={data.total}
              onPage={setPage}
              summary={summary}
              loading={list.isFetching}
              empty={emptyState}
            />
          </>
        )}
      </div>

      <LogReturnModal open={logOpen} setOpen={setLogOpen} onSubmit={submitLog} submitting={logReturn.isLoading} error={logError} />
    </div>
  );
}
